import Image from 'next/image';
import Link from 'next/link';
import { cva, type VariantProps } from 'class-variance-authority';
import commonStyles from '@/app/(website)/commonStyles.module.scss';
import styles from './articleCard.module.scss';
import { formatDate } from '@/services/date';
import { formatLinkUrl } from '@/services/formatUtils';
import {
  ArticleData,
  CaseStudyData,
  CardResource,
  isCalculatorResource,
  isCustomResource
} from '@/services/cms/types';
import { resolveDisplayDate } from '@/services/cms/resolveDisplayDate';
import { ArrowRightLong, Button } from '@baseline/ui';
import { highlightPlaceholdersElement } from '@/services/cmsFormatUtils';
import { resolveCardImage } from '@/services/resolveCardImage';

const cardClass = cva(styles.card, {
  variants: {
    variant: {
      vertical: '',
      horizontal: styles.horizontal,
      horizontalSmall: styles.horizontalSmall,
      navFeatured: styles.noHover
    },
    background: {
      dark: styles.dark
    }
  },
  defaultVariants: {
    variant: 'vertical'
  }
});

const titleClass = cva(styles.title, {
  variants: {
    variant: {
      vertical: `${commonStyles.sectionSubheadingH6} ${styles.title}`,
      horizontal: `${commonStyles.sectionSubheadingH2} ${styles.title}`,
      horizontalSmall: `${commonStyles.sectionSubheadingH6} ${styles.title}`,
      navFeatured: styles.navFeaturedTitle
    }
  },
  defaultVariants: {
    variant: 'vertical'
  }
});

const descriptionClass = cva(styles.description, {
  variants: {
    variant: {
      vertical: `${commonStyles.sectionBodySmall} ${styles.description} ${commonStyles.textOverflowEllipsis}`,
      horizontal: `${commonStyles.sectionBody} ${styles.description} ${commonStyles.textOverflowEllipsis}`,
      horizontalSmall: `${commonStyles.sectionBodySmall} ${styles.description} ${commonStyles.textOverflowEllipsis}`,
      navFeatured: ''
    }
  },
  defaultVariants: {
    variant: 'vertical'
  }
});

export type ArticleCardProps = {
  readonly article: CardResource;
  readonly metaDisplay?: 'date' | 'type' | 'none';
  hideDescription?: boolean;
  showArrowButton?: boolean;
} & VariantProps<typeof cardClass>;

export type ResourceType =
  | NonNullable<
      ArticleData['article']['type'] | CaseStudyData['caseStudy']['type']
    >
  | 'calculator'
  | 'custom';

const displayType: Record<ResourceType, string> = {
  article: 'Article',
  spotlight: 'Product Spotlight',
  interview: 'Interview',
  caseStudy: 'Case Study',
  calculator: 'Calculator',
  custom: 'Custom'
};

const getCardDescription = (article: CardResource) => {
  if (!('description' in article)) return null;
  return article.description;
};

const getCardTitle = (article: CardResource) => {
  if (isCalculatorResource(article)) return article.header;
  if (isCustomResource(article)) return article.custom_title;
  return article.title;
};

const getCardType = (article: CardResource): ResourceType => {
  if (isCalculatorResource(article)) return 'calculator';
  if (isCustomResource(article)) return 'custom';
  return article.type ?? 'article';
};

const getCardHref = (article: CardResource) => {
  if (isCustomResource(article)) {
    return formatLinkUrl(article.custom_link?.internal_url);
  }

  const type = getCardType(article);
  const slug = article.slug;

  if (type === 'calculator') return `/calculators/${slug}`;
  if (type === 'caseStudy') return `/resources/case-studies/${slug}`;
  if (type === 'spotlight') return `/resources/spotlight/${slug}`;
  if (type === 'interview') return `/resources/interviews/${slug}`;
  return `/resources/articles/${slug}`;
};

const getCardDate = (article: CardResource) => {
  if (isCustomResource(article)) return '';
  return resolveDisplayDate(article);
};

const ArticleCard = ({
  article,
  variant,
  metaDisplay = 'date',
  hideDescription = false,
  showArrowButton = false,
  background
}: ArticleCardProps) => {
  const description = getCardDescription(article);
  const title = getCardTitle(article);
  const type = getCardType(article);

  const { src: imageSrc, alt, objectPosition } = resolveCardImage({ article });

  const resolvedDate = getCardDate(article);
  const displayDate = resolvedDate ? formatDate(resolvedDate) : '';
  const href = getCardHref(article);

  return (
    <Link
      href={href}
      className={cardClass({ variant, background })}
      aria-label="article link"
    >
      <div className={styles.imageWrapper}>
        {imageSrc && (
          <Image
            src={imageSrc}
            alt={alt}
            width={393}
            height={206}
            className={styles.image}
            style={{ objectPosition }}
          />
        )}
      </div>

      <div className={styles.content}>
        {metaDisplay === 'type' && displayType[type] && (
          <span
            className={`${styles.type} ${commonStyles.sectionSubheadingH7}`}
          >
            {displayType[type]}
          </span>
        )}

        {metaDisplay === 'date' && displayDate && (
          <time
            className={`${styles.date} ${commonStyles.sectionSubheadingH7}`}
            dateTime={displayDate}
          >
            {displayDate}
          </time>
        )}

        <h3 className={titleClass({ variant })}>
          {highlightPlaceholdersElement(title || '')}
        </h3>

        {description && !hideDescription && (
          <p className={descriptionClass({ variant })}>{description}</p>
        )}
        {showArrowButton && (
          <Button
            size="icon"
            className={styles.ctaButton}
            colorScheme="tertiary"
            aria-label="Next testimonial"
          >
            <ArrowRightLong style={{ width: '1rem', height: '1rem' }} />
          </Button>
        )}
      </div>
    </Link>
  );
};

export default ArticleCard;
